import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import { SelectField, CheckboxField } from "@/components/ui/FormFields";
import {
  getPrivacySettings,
  updatePrivacySettings,
  getNotificationPrefs,
  updateNotificationPrefs,
  type PrivacySettings,
  type NotificationPrefs,
} from "@/api/settings";

const PROFILE_VISIBILITY_OPTIONS = [
  { value: "everyone", label: "Everyone" },
  { value: "indians_only", label: "Indians only" },
  { value: "same_state_only", label: "Same state only" },
  { value: "connections_only", label: "Connections only" },
];

const LOCATION_VISIBILITY_OPTIONS = [
  { value: "exact_city", label: "Exact city" },
  { value: "state_only", label: "State only" },
  { value: "country_only", label: "Country only" },
  { value: "hidden", label: "Hidden" },
];

const NOTIF_FIELDS: { key: keyof NotificationPrefs; label: string }[] = [
  { key: "events", label: "Nearby events" },
  { key: "services", label: "New services / providers" },
  { key: "newsletter", label: "Weekly newsletter" },
  { key: "directMessages", label: "Direct messages" },
  { key: "communityAlerts", label: "Community alerts (state/language)" },
  { key: "questionReplies", label: "Replies to my questions" },
  { key: "mentions", label: "When someone mentions me" },
];

export default function SettingsLab() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings & Privacy"
        subtitle="Privacy visibility controls (these drive privacy-aware discovery) and notification preferences."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Settings" }]}
      />
      <PrivacySection />
      <NotificationsSection />
    </div>
  );
}

function PrivacySection() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["settings", "privacy"],
    queryFn: getPrivacySettings,
  });
  const [local, setLocal] = useState<PrivacySettings | null>(null);
  useEffect(() => {
    if (data?.data) setLocal(data.data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (patch: Partial<PrivacySettings>) => updatePrivacySettings(patch),
    onSuccess: (res) => {
      setLocal(res.data);
      qc.invalidateQueries({ queryKey: ["settings", "privacy"] });
    },
  });

  function set<K extends keyof PrivacySettings>(key: K, value: PrivacySettings[K]) {
    if (!local) return;
    setLocal({ ...local, [key]: value });
    mutation.mutate({ [key]: value } as Partial<PrivacySettings>);
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Privacy</h2>
        <SaveStatus
          pending={mutation.isPending}
          error={mutation.isError}
          ok={mutation.isSuccess}
        />
      </div>
      {isLoading || !local ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <RetryNote onRetry={refetch} label="privacy settings" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Profile visibility"
              hint="Who can see your profile in discovery and search"
              value={local.profileVisibility}
              onChange={(e) =>
                set("profileVisibility", e.target.value as PrivacySettings["profileVisibility"])
              }
              options={PROFILE_VISIBILITY_OPTIONS}
            />
            <SelectField
              label="Location visibility"
              hint="How precisely your location appears on your profile"
              value={local.locationVisibility}
              onChange={(e) =>
                set("locationVisibility", e.target.value as PrivacySettings["locationVisibility"])
              }
              options={LOCATION_VISIBILITY_OPTIONS}
            />
          </div>
          {/*
            Turning this off removes the member from event guest lists and
            matches-going counts. Mutual waves never disclose contact details.
          */}
          <CheckboxField
            label="Show my name on event guest lists and count me as going"
            checked={local.showAttendance}
            onChange={(e) => set("showAttendance", e.target.checked)}
          />
          {/*
            Standing display consent — opt-IN, all default off. Consent to fill
            an onboarding field is not consent to wear it publicly. There is
            deliberately no religion/community toggle here: those are never
            standing axes, and a toggle must not exist for something that must
            never be enabled.
          */}
          <CheckboxField
            label={'Show my immigration status on replies (immigration threads only, e.g. "On H-1B")'}
            checked={local.standingShowVisaStatus}
            onChange={(e) => set("standingShowVisaStatus", e.target.checked)}
          />
          <CheckboxField
            label={'Show my family roots on replies (posts about those states, e.g. "Roots in Kerala")'}
            checked={local.standingShowAdditionalRoots}
            onChange={(e) => set("standingShowAdditionalRoots", e.target.checked)}
          />
          <CheckboxField
            label={'Show where I grew up on replies (posts about that state, e.g. "Raised in Chennai")'}
            checked={local.standingShowRaisedIn}
            onChange={(e) => set("standingShowRaisedIn", e.target.checked)}
          />
          {/*
            Opt-IN, off by default. Fixed words only: never the household's
            name, its size or its members.
          */}
          <CheckboxField
            label={'Say on my Parichay that I belong to a household ("Part of a Ghar on WeDeshi")'}
            checked={local.showHouseholdOnParichay}
            onChange={(e) => set("showHouseholdOnParichay", e.target.checked)}
          />
        </div>
      )}
    </section>
  );
}

function NotificationsSection() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["settings", "notifications"],
    queryFn: getNotificationPrefs,
  });
  const [local, setLocal] = useState<NotificationPrefs | null>(null);
  useEffect(() => {
    if (data?.data) setLocal(data.data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (patch: Partial<NotificationPrefs>) =>
      updateNotificationPrefs(patch),
    onSuccess: (res) => {
      setLocal(res.data);
      qc.invalidateQueries({ queryKey: ["settings", "notifications"] });
    },
  });

  function toggle(key: keyof NotificationPrefs, value: boolean) {
    if (!local) return;
    setLocal({ ...local, [key]: value });
    mutation.mutate({ [key]: value } as Partial<NotificationPrefs>);
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Notification preferences
        </h2>
        <SaveStatus
          pending={mutation.isPending}
          error={mutation.isError}
          ok={mutation.isSuccess}
        />
      </div>
      {isLoading || !local ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <RetryNote onRetry={refetch} label="notification preferences" />
      ) : (
        <div className="space-y-2">
          {NOTIF_FIELDS.map((f) => (
            <CheckboxField
              key={f.key}
              label={f.label}
              checked={Boolean(local[f.key])}
              onChange={(e) => toggle(f.key, e.target.checked)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SaveStatus({
  pending,
  error,
  ok,
}: {
  pending: boolean;
  error: boolean;
  ok: boolean;
}) {
  if (pending) return <span className="text-xs text-gray-400">Saving…</span>;
  if (error) return <Badge variant="red">Save failed</Badge>;
  if (ok) return <Badge variant="green">Saved</Badge>;
  return null;
}

function RetryNote({
  onRetry,
  label,
}: {
  onRetry: () => void;
  label: string;
}) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
      Failed to load {label}.{" "}
      <button onClick={onRetry} className="underline font-medium">
        Retry
      </button>
    </div>
  );
}
